import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth, RequireFlat, RequireRole } from './components/Guards';
import { Splash } from './components/ui';
import { DemoBar } from './components/demo';
import { CatalogScope, usePublicData } from './lib/hooks';
import SignIn from './pages/SignIn';
import Claim from './pages/Claim';
import LinkFlat from './pages/LinkFlat';
import Home from './pages/Home';
import Book from './pages/Book';
import Welcome from './pages/Welcome';
import AuthCallback from './pages/AuthCallback';
import { useAuth } from './lib/auth';
import { pageChanged } from './lib/updates';
import OrderPage from './pages/OrderPage';
import PassPage from './pages/PassPage';
import Account from './pages/Account';
import PublicPasses from './pages/PublicPasses';
import Venues from './pages/Venues';

// Staff screens load separately so residents' phones download less.
const Counter = lazy(() => import('./pages/Counter'));
const CommitteeLayout = lazy(() => import('./pages/committee/Layout'));
const Payments = lazy(() => import('./pages/committee/Payments'));
const Statement = lazy(() => import('./pages/committee/Statement'));
const Flats = lazy(() => import('./pages/committee/Flats'));
const FlatDetail = lazy(() => import('./pages/committee/FlatDetail'));
const FlatBook = lazy(() => import('./pages/committee/FlatBook'));
const FlatPrint = lazy(() => import('./pages/committee/FlatPrint'));
const Kitchen = lazy(() => import('./pages/committee/Kitchen'));
const Lists = lazy(() => import('./pages/committee/Lists'));
const People = lazy(() => import('./pages/committee/People'));
const Setup = lazy(() => import('./pages/committee/Setup'));
const Events = lazy(() => import('./pages/committee/Events'));
const CommitteeLive = lazy(() => import('./pages/committee/Live'));
const Programme = lazy(() => import('./pages/committee/Programme'));
// An event's own page (programme, food, contacts), open to anyone.
const EventPage = lazy(() => import('./pages/EventPage'));
const LiveBoard = lazy(() => import('./pages/LiveBoard'));
// Past events open now and then, so they load separately too.
const PastEventList = lazy(() => import('./pages/History').then((m) => ({ default: m.PastEventList })));
const PastEventPage = lazy(() => import('./pages/History').then((m) => ({ default: m.PastEventPage })));

const resident = (el: React.ReactElement) => <RequireAuth><RequireFlat>{el}</RequireFlat></RequireAuth>;
// Staff screens can pick a past event, so they load every event's meals and menus.
const staff = (roles: Parameters<typeof RequireRole>[0]['roles'], el: React.ReactElement) =>
  <RequireAuth><RequireRole roles={roles}><CatalogScope.Provider value="all">{el}</CatalogScope.Provider></RequireRole></RequireAuth>;

export default function App() {
  return (
    <BrowserRouter>
      <PageChanges />
      <TestModeBanner />
      <DemoBar />
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/claim" element={<Navigate to="/link" replace />} />
          <Route path="/join" element={<Claim />} />
          <Route path="/f/:token" element={<PublicPasses />} />
          <Route path="/venues" element={<Venues />} />
          <Route path="/live" element={<LiveBoard />} />
          <Route path="/e/:eventId" element={<EventPage />} />
          <Route path="/link" element={<RequireAuth><LinkFlat /></RequireAuth>} />
          <Route path="/" element={<Root />} />
          <Route path="/book" element={<Book />} />
          <Route path="/orders/:code" element={resident(<OrderPage />)} />
          <Route path="/pass/:sittingId" element={resident(<PassPage />)} />
          <Route path="/account" element={resident(<Account />)} />
          <Route path="/history" element={resident(<PastEventList />)} />
          <Route path="/history/:eventId" element={resident(<PastEventPage />)} />
          <Route path="/counter" element={staff(['caterer', 'committee'], <Counter />)} />
          <Route path="/committee" element={staff(['committee', 'caterer', 'helpdesk', 'programme'], <CommitteeLayout />)}>
            <Route index element={<Payments />} />
            <Route path="flats" element={<Flats />} />
            <Route path="flats/:id" element={<FlatDetail />} />
            <Route path="flats/:id/book" element={<FlatBook />} />
            <Route path="flats/:id/print" element={<FlatPrint />} />
            <Route path="live" element={<CommitteeLive />} />
            <Route path="statement" element={<Statement />} />
            <Route path="kitchen" element={<Kitchen />} />
            <Route path="lists" element={<Lists />} />
            <Route path="people" element={<People />} />
            <Route path="setup" element={<Setup />} />
            <Route path="events" element={<Events />} />
            <Route path="events/:id" element={<Events />} />
            <Route path="programme" element={<Programme />} />
            <Route path="programme/:eventId" element={<Programme />} />
          </Route>
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/welcome" element={<Navigate to="/link" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/** Signed in: My passes (once in a family). Not signed in: what's on and open for booking, no sign-in needed. */
function Root() {
  const { ready, session } = useAuth();
  if (!ready) return <Splash />;
  return session ? resident(<Home />) : <Welcome />;
}

/** Tells the update check when the app moves to another page (a new version that waited loads then). */
function PageChanges() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) first.current = false;
    else pageChanged();
  }, [pathname]);
  return null;
}

/** On a test copy with Cashfree test keys, every page says so: no real money is taken. */
function TestModeBanner() {
  const { settings } = usePublicData();
  if (!settings.data?.payments_test_mode) return null;
  return (
    <div className="test-banner no-print" role="status">
      <b>Test mode</b> · Online payments use Cashfree's test money: nothing real is charged, and these orders aren't real.
    </div>
  );
}

function NotFound() {
  return (
    <div className="splash">
      <div className="stack center" style={{ justifyItems: 'center', padding: 16 }}>
        <p className="display" style={{ fontSize: 22 }}>Page not found</p>
        <Link className="btn primary" to="/">Go to My passes</Link>
      </div>
    </div>
  );
}
